import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core'
import { Router } from '@angular/router'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src/lib/gb-front-cookie/gb-front-cookie.service'
import { AuctionEntity, AuctionFacade } from 'libs/gb-front/auction/data-access/src'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src'
import { InvoiceFacade } from 'libs/gb-front/checkout/data-access/src/lib/+state/invoice.facade'
import { CheckoutConstants } from 'libs/gb-front/checkout/feature/src/lib/gb-front-checkout-routing.module'
import { ProfileTypeEnum } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.models'
import * as InvoiceActions from 'libs/gb-front/checkout/data-access/src/lib/+state/invoice.actions'

export enum UserTypeEnum {
  UnAuthed = 'unauthed',
}

export enum ConfigKeyEnum {
  Description = 'description',
  CancelText = 'cancelText',
  ShowCheckoutButton = 'showCheckoutButton',
}

@Component({
  selector: 'gb-auction-closed-dialog',
  templateUrl: './gb-auction-closed-dialog.component.html',
  styleUrls: ['./gb-auction-closed-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbAuctionClosedDialogComponent implements OnInit {
  auction$ = this.auctionFacade.auction$
  isAuthenticated$ = this.authFacade.isAuthenticated$
  isVolunteer$ = this.authFacade.isVolunteer$
  hasUnpaidInvoice$ = this.invoiceFacade.hasUnpaidInvoice$

  private configMap = {
    [ProfileTypeEnum.Volunteer]: () => {
      return {
        [ConfigKeyEnum.Description]: 'AUCTION_CLOSED_DIALOG.DESCRIPTION_VOLUNTEER',
        [ConfigKeyEnum.CancelText]: 'AUCTION_CLOSED_DIALOG.CONTINUE_BROWSING',
        [ConfigKeyEnum.ShowCheckoutButton]: true,
      }
    },
    [ProfileTypeEnum.Guest]: (auction: AuctionEntity | null, hasUnpaidInvoice: boolean | null) => {
      return {
        [ConfigKeyEnum.Description]: hasUnpaidInvoice
          ? 'AUCTION_CLOSED_DIALOG.DESCRIPTION_GUEST_OWING'
          : this.hasCustomClosedMessage(auction)
          ? auction!.closed_popup_message
          : 'AUCTION_CLOSED_DIALOG.DESCRIPTION_GUEST_NOT_OWING',
        [ConfigKeyEnum.CancelText]: hasUnpaidInvoice
          ? 'AUCTION_CLOSED_DIALOG.CONTINUE_BROWSING'
          : 'AUCTION_CLOSED_DIALOG.NO_INVOICE_CONTINUE_BROWSING',
        [ConfigKeyEnum.ShowCheckoutButton]: !!hasUnpaidInvoice,
      }
    },
    [UserTypeEnum.UnAuthed]: (auction: AuctionEntity | null) => {
      return {
        [ConfigKeyEnum.Description]: this.hasCustomClosedMessage(auction)
          ? auction!.closed_popup_message
          : 'AUCTION_CLOSED_DIALOG.DESCRIPTION_UNAUTHED',
        [ConfigKeyEnum.CancelText]: 'AUCTION_CLOSED_DIALOG.NO_INVOICE_CONTINUE_BROWSING',
        [ConfigKeyEnum.ShowCheckoutButton]: false,
      }
    },
  }

  constructor(
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private invoiceFacade: InvoiceFacade,
    private gbFrontCookieService: GbFrontCookieService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.gbFrontCookieService.isGuest()) {
      this.invoiceFacade.dispatch(InvoiceActions.loadInvoice())
    }
  }

  getDescription(
    auction: AuctionEntity | null,
    isAuthenticated: boolean | null,
    isVolunteer: boolean | null,
    hasUnpaidInvoice: boolean | null,
  ): string {
    return this.getConfig(auction, isAuthenticated, isVolunteer, hasUnpaidInvoice, ConfigKeyEnum.Description)
  }

  showCheckoutButton(
    auction: AuctionEntity | null,
    isAuthenticated: boolean | null,
    isVolunteer: boolean | null,
    hasUnpaidInvoice: boolean | null,
  ): boolean {
    return this.getConfig(auction, isAuthenticated, isVolunteer, hasUnpaidInvoice, ConfigKeyEnum.ShowCheckoutButton)
  }

  getCancelButtonText(
    auction: AuctionEntity | null,
    isAuthenticated: boolean | null,
    isVolunteer: boolean | null,
    hasUnpaidInvoice: boolean | null,
  ): string {
    return this.getConfig(auction, isAuthenticated, isVolunteer, hasUnpaidInvoice, ConfigKeyEnum.CancelText)
  }

  getConfig(
    auction: AuctionEntity | null,
    isAuthenticated: boolean | null,
    isVolunteer: boolean | null,
    hasUnpaidInvoice: boolean | null,
    key: ConfigKeyEnum,
  ): any {
    if (isVolunteer) {
      return this.configMap[ProfileTypeEnum.Volunteer]()[key]
    } else if (isAuthenticated) {
      return this.configMap[ProfileTypeEnum.Guest](auction, hasUnpaidInvoice)[key]
    } else {
      return this.configMap[UserTypeEnum.UnAuthed](auction)[key]
    }
  }

  hasCustomClosedMessage(auction: AuctionEntity | null): boolean {
    return !!auction && !!auction.closed_popup_message
  }

  navigateToCheckout(auction: AuctionEntity | null): void {
    this.router.navigate([`${auction?.url_name}/${CheckoutConstants.CHECKOUT}`])
  }
}
